import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import tenantMediaService, { GalleryResponse } from '../../services/tenantMedia.ts';
import './TenantMedia.css';

interface GalleryManagerProps {
  tenantId: number;
  locationId: number; // required for uploads
}

const GalleryManager: React.FC<GalleryManagerProps> = ({ tenantId, locationId }) => {
  const [gallery, setGallery] = useState<GalleryResponse>({ 
    tenantId, 
    images: [], 
    count: 0, 
    maxAllowed: 12 
  });
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadGallery();
  }, [tenantId]);

  const loadGallery = async () => {
    try {
      // Get admin gallery info
      const adminGallery = await tenantMediaService.listGallery(tenantId);
      // Get public URLs for preview
      const publicGallery = await tenantMediaService.getPublicGallery(tenantId);
      
      setGallery({
        ...adminGallery,
        images: publicGallery.images || []
      });
    } catch (error) {
      console.error('Failed to load gallery:', error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Check total count
    const remainingSlots = (gallery.maxAllowed || 12) - gallery.count;
    if (files.length > remainingSlots) {
      toast.error(`You can only upload ${remainingSlots} more image(s). Maximum is ${gallery.maxAllowed || 12}.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      // Check type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error(`${file.name}: Must be JPG, JPEG, or PNG`);
        continue;
      }

      // Check size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: File size must not exceed 5MB`);
        continue;
      }

      validFiles.push(file);
    }

    setSelectedFiles(validFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Please select images first');
      return;
    }
    if (!locationId) {
      toast.error('Please select a location');
      return;
    }

    setUploading(true);
    try {
      const results = await tenantMediaService.uploadGalleryImages(locationId, selectedFiles);
      
      // Count successes and failures
      const successes = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success);

      if (successes > 0) {
        toast.success(`${successes} image(s) uploaded successfully`);
      }

      failures.forEach(failure => {
        toast.error(failure.message);
      });

      // Reload gallery
      await loadGallery();
      
      // Clear selection
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageUrl: string) => {
    // Extract filename from URL
    const fileName = imageUrl.split('/').pop();
    if (!fileName) return;

    if (!window.confirm(`Are you sure you want to delete this image?`)) {
      return;
    }

    setDeleting(fileName);
    try {
      await tenantMediaService.deleteGalleryImage(tenantId, fileName);
      toast.success('Image deleted successfully');
      await loadGallery();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete image');
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="gallery-manager">
      <h3 className="section-title">
        <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Photo Gallery ({gallery.count}/{gallery.maxAllowed || 12})
      </h3>

      <div className="upload-section">
        <div className="file-input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            multiple
            onChange={handleFileSelect}
            disabled={uploading || gallery.count >= (gallery.maxAllowed || 12)}
            className="file-input"
            id="gallery-file-input"
          />
          <label htmlFor="gallery-file-input" className="file-input-label">
            <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {selectedFiles.length > 0 
              ? `${selectedFiles.length} file(s) selected` 
              : gallery.count >= (gallery.maxAllowed || 12)
                ? 'Gallery is full'
                : 'Choose images (JPG/PNG, max 5MB each)'}
          </label>
          
          {selectedFiles.length > 0 && (
            <div className="file-info">
              <span className="file-count">
                {selectedFiles.length} file(s) - {formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))}
              </span>
              <button 
                onClick={() => {
                  setSelectedFiles([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="btn-clear"
                type="button"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploading || gallery.count >= (gallery.maxAllowed || 12)}
          className="btn btn-primary"
        >
          {uploading ? (
            <>
              <span className="spinner-small"></span>
              Uploading...
            </>
          ) : (
            <>
              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Images
            </>
          )}
        </button>
      </div>

      {gallery.count > 0 ? (
        <div className="gallery-grid">
          {gallery.images.map((imageUrl, index) => {
            const fileName = imageUrl.split('/').pop() || '';
            return (
              <div key={index} className="gallery-item">
                <img 
                  src={imageUrl} 
                  alt={`Gallery ${index + 1}`}
                  className="gallery-thumb"
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <button
                    onClick={() => window.open(imageUrl, '_blank')}
                    className="btn-view"
                    title="View full size"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(imageUrl)}
                    disabled={deleting === fileName}
                    className="btn-delete"
                    title="Delete image"
                  >
                    {deleting === fileName ? (
                      <span className="spinner-tiny"></span>
                    ) : (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-gallery">
          <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>No images uploaded yet</p>
          <p className="text-muted">Upload images to create a photo gallery for this restaurant</p>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
